import React, { useState, useEffect } from 'react';
import { Search, X, ArrowRight, FileText, Ticket, FolderKanban, Building2, Receipt, Award } from 'lucide-react';

interface GlobalSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

export const GlobalSearchModal: React.FC<GlobalSearchModalProps> = ({ isOpen, onClose, onNavigate }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults([]);
      return;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(query.trim())}`);
        if (res.ok) {
          const data = await res.json();
          setResults(data.results || []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [query]);

  if (!isOpen) return null;

  const getIcon = (type: string) => {
    switch (type) {
      case 'Ticket':
        return <Ticket className="w-4 h-4 text-amber-600" />;
      case 'Project':
        return <FolderKanban className="w-4 h-4 text-blue-600" />;
      case 'Invoice':
      case 'Consolidated Invoice':
        return <Receipt className="w-4 h-4 text-emerald-600" />;
      case 'Branch':
        return <Building2 className="w-4 h-4 text-indigo-600" />;
      case 'Completion Certificate':
        return <Award className="w-4 h-4 text-purple-600" />;
      default:
        return <FileText className="w-4 h-4 text-slate-500" />;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4 bg-slate-900/60 backdrop-blur-xs">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        <div className="flex items-center px-4 py-3 border-b border-slate-100 bg-slate-50">
          <Search className="w-5 h-5 text-slate-400 mr-3" />
          <input
            type="text"
            className="w-full bg-transparent text-slate-800 placeholder-slate-400 text-sm focus:outline-none"
            placeholder="Search Tickets (TKT-...), Projects (NB-...), Invoices, UBL Branches, POs..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          {query && (
            <button onClick={() => setQuery('')} className="p-1 text-slate-400 hover:text-slate-600 mr-2">
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="text-xs font-medium text-slate-500 bg-slate-200 px-2 py-1 rounded-sm hover:bg-slate-300"
          >
            ESC
          </button>
        </div>

        <div className="max-h-96 overflow-y-auto p-2">
          {loading && (
            <div className="py-8 text-center text-sm text-slate-500">Searching across ERP records...</div>
          )}

          {!loading && query && results.length === 0 && (
            <div className="py-8 text-center text-sm text-slate-500">
              No matching records found for "{query}".
            </div>
          )}

          {!loading && !query && (
            <div className="p-4 text-xs text-slate-400 text-center">
              Type ticket number, project name, branch code, or invoice reference to jump directly.
            </div>
          )}

          {!loading && results.map((item, idx) => (
            <div
              key={idx}
              onClick={() => {
                onNavigate(item.link);
                onClose();
              }}
              className="flex items-center justify-between p-3 rounded-lg hover:bg-blue-50/70 cursor-pointer transition-colors border border-transparent hover:border-blue-100 group"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-slate-100 group-hover:bg-white transition-colors">
                  {getIcon(item.type)}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600">
                      {item.type}
                    </span>
                    <span className="text-sm font-medium text-slate-800 group-hover:text-blue-700">
                      {item.title}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{item.subtitle}</p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
